import React, { useState, useEffect } from 'react';
import Section from 'components/form/section/section';
import Label from 'components/form/label/label';
import { Field, Formik } from 'formik';
import classNames from 'classnames';
import * as Yup from 'yup'; // Needed for custom form validation

import ComplexElement from './complexElement';
import * as Constants from '../../../components/wizard/constants';
import Error from './error';
import Info from '../info/info';
import NavBottom from './navBottom';
import styles from './builder.module.scss';

const  Builder = React.forwardRef((props,ref) =>{

  const [schemaShape, setSchemaShape] = useState(props.schemaShape);
  // Used to update schema
  const [doUpdate, setDoUpdate] = useState(false);

  const formRef = React.useRef(null);

  useEffect(() => {
    // TODO add logic dealing with lifecycle stuff to get rid of errors
  })

  React.useImperativeHandle(ref, () => ({
     submitForm: () => {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  }));

  /* Updates which fields are required based on conditionally dependent
   * information defined in `step*.js`.
   *
   * The `conditionaler` field is set to 'true' if an element conditions other
   * elements.
   *
   * The `conditional` field is populated with values identifying a
   * `conditionaler` for conditionally controlled elements.
   */
  function updateSchemaWithDependencies(values, callingElement) {
    // `values` comes from Formik.
    // `element` is the element from which this function was called.
    //
    // Note that because of async things, `values` *will* be representing an
    // earlier state... (see further comments below); this logic issue is to
    // be resolved.

    let schemaShapeUpdates = {};
    props.elements.forEach(element => {
      // Add conditional logic, if any
      if (element.conditional) {
        // If one of the selected form's selected values is equal to the
        // conditional triggering condition...
        setDoUpdate(true);

        if (
          callingElement.name === element.conditional.name &&
          values[element.conditional.name] === element.conditional.value
        ) {
          // Update element's children with 'required'
          if (element.children) {
            // e.g. mailing address fields
            element.children.forEach(elem => {
              // Ad hoc solution to ignore 'mailingAddress2 field...'
              if (elem.name.includes('2')) {
                return; // -> do nothing to update that element's schema
              }
              switch (elem.name) {
                case 'MailingAddress1':
                  schemaShapeUpdates[elem.name] = Yup.string()
                    .matches(Constants.NO_LEADING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .matches(Constants.NO_TRAILING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .max(
                      Constants.MAX_CHARS_FOR_FIELD,
                      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
                    )
                    .required('Required');
                  break;
                case 'MailingCity':
                  schemaShapeUpdates[elem.name] = Yup.string()
                    .matches(Constants.NO_LEADING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .matches(Constants.NO_TRAILING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .matches(/^[A-Za-z\s]*$/, {
                      message: 'Only letters and spaces allowed.'
                    })
                    .max(
                      Constants.MAX_CHARS_FOR_FIELD,
                      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
                    )
                    .required('Required');
                  break;
                case 'MailingZip':
                  schemaShapeUpdates[elem.name] = Yup.string()
                    .matches(Constants.NO_LEADING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .matches(Constants.NO_TRAILING_WHITE_SPACE, {
                      message: Constants.TRIM_WHITE_SPACE_MESSAGE
                    })
                    .matches(/^[0-9]{5}(?:-[0-9]{4})?$/, {
                      message: 'Invalid Zip Code'
                    })
                    .required('Required');
                  break;
                case 'MailingState':
                  schemaShapeUpdates[elem.name] = Yup.string().required(
                    'Required'
                  );
                  break;
                default:
                  schemaShapeUpdates[elem.name] = Yup.string().required(
                    'required'
                  );
              }
            });
          } else {
            // e.g. step5 required steps
            schemaShapeUpdates[element.name] = Yup.string().required(
              `"${element.label}" is required` // TODO update custom validation messages
            );
          }
        } else if (
          callingElement.name === element.conditional.name &&
          values[element.conditional.name] !== element.conditional.value
        ) {
          // Restore "default" required settings
          if (element.children) {
            element.children.forEach(child => {
              schemaShapeUpdates[child.name] = props.schemaShape[child.name];
            });
          } else {
            // Non-nested element
            schemaShapeUpdates[element.name] = props.schemaShape[element.name];
          }
        } else {
          // Other conditional elements, not selected with current click
          // Do nothing, shape stored in state will be used
        }
      }
    });

    if (doUpdate) {
      // setState sets schema *shape* (not return an actual schema; this is
      // created in the Formik props)

      // iterate through schemaShapeUpdates, delete existing properties on state object
      Object.keys(schemaShapeUpdates).forEach(schemaKey => {
        if (schemaShape[schemaKey]) {
          // Delete schema key/values we will be replacing
          delete schemaShape[schemaKey];
        }
      });
      // then assign new state

      // Combines old state with newly updated fields
      setSchemaShape({
        ...schemaShape,
        ...schemaShapeUpdates
      });
    }
  }

  return (
    <Formik
      initialErrors={false}
      initialValues={props.initialValues}
      validationSchema={Yup.object().shape(schemaShape)}
      validateOnBlur={false}
      validateOnChange={true}
      onSubmit={values => {
        props.updateValues(values);
        props
          .onNext() // Advance to next step in Wizard
          .catch(e => {
            console.log('Something went wrong', e);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue
      }) => {
        return (
          <form onSubmit={handleSubmit} ref={formRef}>
            {props.elements.map(element => {
              if (element.children) {
                return (
                  // Elements with children are called "complex"
                  <ComplexElement
                    key={"foo" + element.name}
                    element={element}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    values={values}
                  />
                );
              } else {
                const hasError = touched[element.name] && errors[element.name];
                const error = hasError;

                if (element.conditional) {
                  if (
                    values[element.conditional.name] !==
                    element.conditional.value
                  ) {
                    return null;
                  }
                }

                // Create elements with specified component (includes select/dropdowns)
                // This is how Prompts (YES/NO) components are rendered!
                if (element.labelOnly) {
                  return (<Section key={element.name}>
                    {element.label && (
                      <Label>
                        <span className={styles.agentFormLabel}>{element.label}</span>
                      </Label>
                    )}
                  </Section>)
                }
                else if (element.Component) {
                  const Component = element.Component;
                  return (
                    <Section key={element.name}>
                      {element.label && (
                        <Label>
                          <span className={styles.agentFormLabel}>{element.label}</span>
                        </Label>
                      )}
                      <Component
                        name={element.name}
                        onChange={e => {
                          // Do normal things
                          handleChange(e);

                          // Update schema if necessary
                          /* Temporary fix for ASYNC PROBLEMS
                           * Hack where values is "optmistically" changed...
                           * Still need to fix the async logic;
                           * But this solution allows the
                           * updateSchemaWithDependencies function to be
                           * written in a more sensible way.
                           */
                          if (element.conditionaler) {
                            // Later, patchedValues shouldn't be used here
                            // Rather, async logic to get `values` directly
                            // should be used.
                            let patchedValues = values; // adjust this
                            // Update to "future value"
                            if(!element?.keepAsItIs){
                              patchedValues[element.name] = !patchedValues[
                                element.name
                              ];  
                            }
                            updateSchemaWithDependencies(
                              patchedValues,
                              element
                            );
                          }
                        }}
                        onBlur={handleBlur}
                        info={element.infoMessage}
                        value={values[element.name]}
                        error={hasError}
                        {...element.props}
                      />
                      <Error>{error}</Error>
                    </Section>
                  );
                } else {
                  return (
                    <Section key={element.name}>
                      <div className="form-group">
                        {element.label && (
                          <Label>
                            <span className={styles.agentFormLabel}>{element.label}</span>
                          </Label>
                        )}
                        {element.label === 'Please Explain' ? (
                          // The conditional fields in "Agency Eligibility Questionnaire" go here
                          <Field
                            name={element.name}
                            as={'textarea'}
                            rows={'5'}
                            placeholder={`${element.placeholder}`}
                            className={classNames('form-control', {
                              'is-invalid':
                                touched[element.name] && errors[element.name]
                            })}
                            maxLength={element?.maxLength || null}
                            {...element.props}
                          />
                        ) : (
                          <Field
                            name={element.name}
                            placeholder={`${element.placeholder}`}
                            className={classNames('form-control', {
                              'is-invalid':
                                touched[element.name] && errors[element.name]
                            })}
                            onBlur={(e) => {
                              const val = (e.target.value || '').replace(/\s+/gi, ' ');
                              setFieldValue(element.name, val.trim());
                              handleBlur(e);
                            }}
                            maxLength={element?.maxLength || null}
                            {...element.props}
                          />
                        )}
                        {element.infoMessage ? (
                          <div className="input-group-append">
                            <Info>{element.infoMessage}</Info>
                          </div>
                        ) : null}
                      </div>
                      <Error>{error}</Error>
                    </Section>
                  );
                }
              }
            })}
            <NavBottom
              loading={props.loading}
              step={props.step}
              setStep={props.setStep}
              isSubmitting={isSubmitting}
              isSubmitButtonDisabled={props.isSubmitButtonDisabled}
            />
          </form>
        );
      }}
    </Formik>
  );
})

export default Builder;
